import { Banknote, Truck } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Breadcrumb,
  Button,
  ConfirmDialog,
  ErrorState,
  Money,
  PageHeader,
  PageShell,
  Spinner,
} from '@/shared/ui';

import { OfflineCartPanel } from '../components/OfflineCartPanel';
import { OfflineCouponPanel } from '../components/OfflineCouponPanel';
import { OfflineCustomerForm } from '../components/OfflineCustomerForm';
import { OfflineDeliveryAddressForm } from '../components/OfflineDeliveryAddressForm';
import { OfflineFulfillmentPicker } from '../components/OfflineFulfillmentPicker';
import { OfflineProductSearch } from '../components/OfflineProductSearch';
import { OfflineSaleReceiptDialog } from '../components/OfflineSaleReceiptDialog';
import { OfflineStorePicker } from '../components/OfflineStorePicker';
import { useOfflineSaleState } from '../hooks/useOfflineSaleState';

export function OfflineSalePage() {
  const { t } = useTranslation('offlineOrders');
  const { t: tCommon } = useTranslation('common');
  const sale = useOfflineSaleState();
  const barcodeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sale.pickerOpen || !sale.storeId) return;
    barcodeRef.current?.focus();
  }, [sale.pickerOpen, sale.storeId]);

  useEffect(() => {
    if (sale.pickerOpen || !sale.storeId) return;
    const active = document.activeElement;
    if (active === document.body || active === document.documentElement || active == null) {
      barcodeRef.current?.focus();
    }
  }, [sale.pickerOpen, sale.session.cart.items.length, sale.storeId]);

  if (sale.cartQuery.isLoading) {
    return (
      <PageShell data-page="offline-orders/sale">
        <div className="flex justify-center py-24">
          <Spinner label={t('title')} size={28} />
        </div>
      </PageShell>
    );
  }

  if (sale.cartQuery.isError) {
    return (
      <PageShell data-page="offline-orders/sale">
        <ErrorState onRetry={() => void sale.reloadCart()} />
      </PageShell>
    );
  }

  const showPicker = sale.pickerOpen || !sale.storeId;
  const homeDelivery = sale.fulfillmentType === 'HOME_DELIVERY';
  const storeLabel = sale.selectedStore
    ? `${sale.selectedStore.storeCode} · ${sale.selectedStore.name}`
    : null;
  const pageDescription = storeLabel ? t('sellingAt', { store: storeLabel }) : t('subtitle');

  return (
    <PageShell data-page="offline-orders/sale">
      <Breadcrumb
        items={[
          { label: tCommon('nav.home'), to: '/home' },
          { label: tCommon('nav.offlineSale'), current: true },
        ]}
      />
      <PageHeader
        title={t('title')}
        description={pageDescription}
        actions={
          sale.storeId && !showPicker ? (
            <>
              <Button type="button" variant="secondary" onClick={() => sale.setPickerOpen(true)}>
                {t('store.change')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={sale.session.cart.items.length === 0 && !sale.session.customerPhoneNumber}
                onClick={() => sale.setConfirmClearOpen(true)}
              >
                {t('cart.clear')}
              </Button>
            </>
          ) : null
        }
      />

      {showPicker ? (
        <OfflineStorePicker
          stores={sale.storesQuery.data?.items ?? []}
          isLoading={sale.storesQuery.isLoading}
          isError={sale.storesQuery.isError}
          onRetry={() => void sale.storesQuery.refetch()}
          selectedStoreId={sale.storeId}
          canGoBack={Boolean(sale.storeId)}
          onBack={() => sale.setPickerOpen(false)}
          onSelect={sale.selectStore}
        />
      ) : sale.storeId ? (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_28rem]">
          <OfflineProductSearch
            storeId={sale.storeId}
            disabled={sale.storeMismatch}
            busy={sale.cartBusy}
            barcodeInputRef={barcodeRef}
            onAdd={sale.addProduct}
            onScan={sale.scanBarcode}
          />
          <div className="xl:sticky xl:top-4 xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto">
            <OfflineCartPanel
              session={sale.session}
              disabled={sale.cartBusy}
              mismatch={sale.storeMismatch}
              checkoutError={sale.checkoutError}
              checkingOut={sale.checkingOut}
              fulfillmentType={sale.fulfillmentType}
              lead={
                <OfflineFulfillmentPicker
                  value={sale.fulfillmentType}
                  disabled={sale.cartBusy}
                  onChange={(next) => sale.setFulfillmentType(next)}
                />
              }
              onQuantity={(productId, quantity) => void sale.setQuantity(productId, quantity)}
              onRemove={(productId) => void sale.removeLine(productId)}
              onReload={() => void sale.reloadCart()}
              onCheckout={sale.requestCheckout}
            >
              <OfflineCustomerForm
                session={sale.session}
                snapshot={sale.customerSnapshot}
                phone={sale.phoneDraft}
                name={sale.nameDraft}
                identifying={sale.identifying}
                clearing={sale.clearingCustomer}
                error={sale.identifyError}
                onPhoneChange={sale.setPhoneDraft}
                onNameChange={sale.setNameDraft}
                onIdentify={sale.identify}
                onClear={sale.forgetCustomer}
              />
              {homeDelivery ? (
                <OfflineDeliveryAddressForm
                  value={sale.addressDraft}
                  disabled={sale.cartBusy}
                  onChange={(next) => sale.setAddressDraft(next)}
                />
              ) : null}
              <OfflineCouponPanel
                session={sale.session}
                coupons={sale.couponsQuery.data}
                loading={sale.couponsQuery.isFetching}
                busy={sale.couponBusy}
                appliedCode={sale.appliedCouponCode}
                notice={
                  sale.couponNotice ?? (sale.couponsQuery.isError ? t('coupons.loadFailed') : null)
                }
                preview={sale.couponPreview}
                onPreview={sale.previewCoupon}
                onApply={sale.applyCouponCode}
                onRemove={sale.clearCoupon}
              />
            </OfflineCartPanel>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={sale.confirmClearOpen}
        onOpenChange={sale.setConfirmClearOpen}
        title={t('cart.clearTitle')}
        description={t('cart.clearBody')}
        confirmLabel={t('cart.clearConfirm')}
        confirmVariant="danger"
        isConfirming={sale.clearingCart}
        onConfirm={sale.confirmClearCart}
      />
      <ConfirmDialog
        open={sale.confirmSwitchOpen}
        onOpenChange={sale.setConfirmSwitchOpen}
        title={t('store.switchTitle')}
        description={t('store.switchBody')}
        confirmLabel={t('store.switchConfirm')}
        confirmVariant="danger"
        isConfirming={sale.clearingCart}
        onConfirm={sale.confirmStoreSwitch}
      />
      <ConfirmDialog
        open={sale.confirmCheckoutOpen}
        onOpenChange={sale.setConfirmCheckoutOpen}
        icon={
          <span
            className={
              homeDelivery
                ? 'flex h-12 w-12 items-center justify-center rounded-full bg-info-subtle text-info-strong'
                : 'flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle text-success-strong'
            }
          >
            {homeDelivery ? (
              <Truck aria-hidden="true" className="h-5 w-5" strokeWidth={2} />
            ) : (
              <Banknote aria-hidden="true" className="h-5 w-5" strokeWidth={2} />
            )}
          </span>
        }
        title={homeDelivery ? t('checkout.homeDelivery.confirmTitle') : t('checkout.confirmTitle')}
        description={
          <div className="space-y-3 text-left">
            <p>
              {homeDelivery ? t('checkout.homeDelivery.confirmBody') : t('checkout.confirmBody')}
            </p>
            <p className="flex items-baseline justify-between gap-3 rounded-md bg-surface-muted px-3 py-2 text-fg">
              <span className="text-sm font-medium">{t('cart.payable')}</span>
              <span className="text-base font-semibold tabular-nums">
                <Money amount={sale.session.totalAmountPaise} />
              </span>
            </p>
          </div>
        }
        confirmLabel={homeDelivery ? t('checkout.homeDelivery.confirm') : t('checkout.confirm')}
        isConfirming={sale.checkingOut}
        onConfirm={sale.confirmCheckout}
      />
      <OfflineSaleReceiptDialog
        receipt={sale.receipt}
        onNewSale={() => {
          sale.dismissReceipt();
          barcodeRef.current?.focus();
        }}
      />
    </PageShell>
  );
}
